function parseAimLogDateTimeParts(logDate: string, logTime?: string | null) {
  const dateMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(logDate.trim());
  if (!dateMatch) {
    return null;
  }

  const month = Number(dateMatch[1]);
  const day = Number(dateMatch[2]);
  const year = Number(dateMatch[3]);
  const timeMatch = logTime ? /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(logTime.trim()) : null;
  const hour = timeMatch ? Number(timeMatch[1]) : 12;
  const minute = timeMatch ? Number(timeMatch[2]) : 0;
  const second = timeMatch?.[3] ? Number(timeMatch[3]) : 0;

  return { year, month, day, hour, minute, second };
}

export function getTimeZoneOffsetMs(date: Date, timeZone: string) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'longOffset',
  });
  const parts = formatter.formatToParts(date);
  const zone = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'GMT';
  const match = /^GMT(?:(\+|-)(\d{1,2})(?::(\d{2}))?)?$/.exec(zone);
  if (!match) {
    return 0;
  }

  const sign = match[1] === '-' ? -1 : 1;
  const hours = Number(match[2] ?? 0);
  const minutes = Number(match[3] ?? 0);
  return sign * (hours * 60 + minutes) * 60_000;
}

export function wallClockInTimeZoneToUtcMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
) {
  let guess = Date.UTC(year, month - 1, day, hour, minute, second);

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const offset = getTimeZoneOffsetMs(new Date(guess), timeZone);
    const next = Date.UTC(year, month - 1, day, hour, minute, second) - offset;
    if (Math.abs(next - guess) < 1000) {
      return next;
    }

    guess = next;
  }

  return guess;
}

/** AIM footer dates use MM/DD/YYYY and HH:MM[:SS] in logger-local time. */
export function parseAimLogDateTimeMs(
  logDate: string,
  logTime?: string | null,
  timeZone?: string | null,
) {
  const parts = parseAimLogDateTimeParts(logDate, logTime);
  if (!parts) {
    return null;
  }

  const { year, month, day, hour, minute, second } = parts;
  const trimmedTimeZone = timeZone?.trim();

  if (trimmedTimeZone) {
    return wallClockInTimeZoneToUtcMs(year, month, day, hour, minute, second, trimmedTimeZone);
  }

  const recordedAtMs = new Date(year, month - 1, day, hour, minute, second).getTime();
  return Number.isNaN(recordedAtMs) ? null : recordedAtMs;
}

export function hourInTimeZone(date: Date, timeZone?: string | null) {
  const trimmed = timeZone?.trim();
  if (!trimmed) {
    return date.getHours();
  }

  return Number(
    new Intl.DateTimeFormat('en-US', {
      timeZone: trimmed,
      hour: 'numeric',
      hour12: false,
    }).format(date),
  );
}
